import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { FormattedMessage } from 'react-intl';
import { Button } from '../../../components/atoms/Button/Button';
import { Header } from '../../../components/organisms/Header/Header';
import { ContentBody } from '../../../components/atoms/ContentBody/ContentBody';
import { Footer } from '../../../components/atoms/Footer/Footer';
import { Stack } from '../../../components/atoms/Stack/Stack';
import { Caption } from '../../../components/atoms/Text/Text';
import { ApplicationUnverifiedLabel } from '../../../components/molecules/ApplicationUnverifiedLabel/ApplicationUnverifiedLabel';
import { InformationField } from '../../../components/molecules/InformationField/InformationField';
import { PageView } from '../../../models/analytics';
import { ScreenWrapper } from '../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { messages } from '../../messages';
import { commonMessages } from '../../../components/atoms/CommonMessages/messages';
import { usePageViewEvents as usePageViewEventsImport } from '../../../core/Analytics/usePageViewEvents';
import { H1 } from '../../../components/atoms/Heading/Heading';
import { useUrlQuery } from '../../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { useAppSelector } from '../../../stores/redux-store';
import { useConfiguration } from '../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { ConsentLegalFooter } from './ConsentLegalFooter';

export type PaymentConsentScreenProps = {
  isClientVerified: boolean;
  clientName: string;
  onConsent: () => void;
  usePageViewEvents?: ({ pageView }: { pageView: PageView }) => void;
};

export const PaymentConsentScreen = ({
  isClientVerified,
  clientName,
  onConsent,
  usePageViewEvents = usePageViewEventsImport,
}: PaymentConsentScreenProps) => {
  usePageViewEvents({
    pageView: PageView.PAYMENTS_CONSENT_SCREEN,
  });
  const { clientId } = useUrlQuery();
  const scopesDescriptionsList = useAppSelector(
    (state) => state.oauth.description?.scopesDescriptionsList
  );
  const { data: config } = useConfiguration();

  return (
    <ScreenWrapper data-testid="payment-consent-screen">
      <Header pageView={PageView.PAYMENTS_CONSENT_SCREEN} onBack={undefined} />
      <ContentBody centerVertically centerHorizontally>
        <ApplicationUnverifiedLabel isVerified={isClientVerified} />
        <Stack justifyContent="center">
          <Box pb="normal">
            <H1 align="center">
              <FormattedMessage
                {...messages.paymentConsentScreenTitle}
                values={{
                  clientName,
                }}
              />
            </H1>
          </Box>
          <InformationField iconVariant="verified" isDecorated={false} iconColor="primary">
            <Box>
              <Caption>
                <FormattedMessage {...messages.paymentConsentScreenInfo} />
              </Caption>
            </Box>
          </InformationField>
          <InformationField iconVariant="bank" isDecorated={false} iconColor="primary">
            <Box>
              <Caption>
                <FormattedMessage {...messages.paymentConsentScreenNextStep} />
              </Caption>
            </Box>
          </InformationField>
        </Stack>
        <Footer stickToBottom>
          <Button
            size="large"
            type="submit"
            color="primary"
            variant="contained"
            onClick={onConsent}
            mb="normedium"
          >
            <FormattedMessage {...commonMessages.continue} />
          </Button>
        </Footer>
      </ContentBody>
      <ConsentLegalFooter
        clientId={clientId}
        clientName={clientName}
        featureFlags={config.featureFlags}
        scopesDescriptionsList={scopesDescriptionsList || []}
      />
    </ScreenWrapper>
  );
};
